import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Play, Clock, BookOpen, Home, X, Search } from 'lucide-react';

import API_URL from '@/config/api';
import logger from '@/utils/logger';

// Section configurations
const SECTION_CONFIG = {
  probound: {
    name: 'ProBound',
    color: '#2F5E96',
    logo: 'https://customer-assets.emergentagent.com/job_resin-quote-calc/artifacts/0yxgnasw_IMG_8077.jpeg',
    backPath: '/probound'
  },
  proepoxy: {
    name: 'ProEpoxy',
    color: '#7A0B7A',
    logo: 'https://customer-assets.emergentagent.com/job_76625416-d43e-411a-b4d0-3b73c58f517c/artifacts/ta6bz3y6_IMG_8292.jpeg',
    backPath: '/proepoxy'
  },
  prodecor: {
    name: 'ProDecor',
    color: '#D5AB09',
    logo: 'https://customer-assets.emergentagent.com/job_d9177fe4-220c-4669-9998-86c6e454a508/artifacts/4z707ot7_IMG_8289.jpeg',
    backPath: '/prodecor'
  },
  protraining: {
    name: 'ProTraining',
    color: '#D35400',
    logo: 'https://customer-assets.emergentagent.com/job_resin-quote-calc/artifacts/0yxgnasw_IMG_8077.jpeg',
    backPath: '/training'
  }
};

// Helper to get YouTube video ID and thumbnail
const getYouTubeInfo = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  if (match) {
    return {
      id: match[1],
      thumbnail: `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`,
      embedUrl: `https://www.youtube.com/embed/${match[1]}`
    };
  }
  return null;
};

const VideoLibrary = () => {
  const navigate = useNavigate();
  const { section = 'probound' } = useParams();
  const config = SECTION_CONFIG[section] || SECTION_CONFIG.probound;
  
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, vidRes] = await Promise.all([
        fetch(`${API_URL}/api/videos/categories?section=${section}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/videos?section=${section}`, { credentials: 'include' })
      ]);
      
      if (catRes.ok) {
        const catData = await catRes.json();
        setCategories(catData);
      }
      if (vidRes.ok) {
        const vidData = await vidRes.json();
        setVideos(vidData);
      }
    } catch (error) {
      logger.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  }, [section]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredVideos = videos.filter(video => {
    const matchesCategory = selectedCategory === 'all' || video.category_id === selectedCategory;
    const matchesSearch = !searchTerm || 
      video.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      video.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || 'Uncategorized';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8ECEF] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2F5E96]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      {/* Header */}
      <header className="bg-white shadow-sm border-b-4" style={{ borderColor: config.color }}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(config.backPath)}
              variant="outline"
              className="border-2"
              style={{ borderColor: config.color, color: config.color }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-gray-600 hover:bg-gray-100"
            >
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
          </div>
          <img 
            src={config.logo} 
            alt={`Ox Resin ${config.name}`} 
            className="h-10 md:h-12"
          />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Title & Search */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#27292E] flex items-center gap-3">
              <BookOpen className="w-8 h-8" style={{ color: config.color }} />
              {config.name} Training Videos
            </h1>
            <p className="text-gray-500 mt-1">Learn installation techniques from our expert guides</p>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search videos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border-2 rounded-xl focus:outline-none"
              style={{ '--tw-ring-color': config.color }}
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 flex-wrap mb-6">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-xl font-medium transition-colors ${
              selectedCategory === 'all'
                ? 'text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border'
            }`}
            style={selectedCategory === 'all' ? { backgroundColor: config.color } : {}}
          >
            All Videos ({videos.length})
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                selectedCategory === cat.id
                  ? 'text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border'
              }`}
              style={selectedCategory === cat.id ? { backgroundColor: config.color } : {}}
            >
              {cat.name} ({videos.filter(v => v.category_id === cat.id).length})
            </button>
          ))}
        </div>

        {/* Videos Grid */}
        {filteredVideos.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No videos found</h3>
            <p className="text-gray-500">
              {searchTerm ? 'Try a different search term' : 'Videos will appear here once added by an admin'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVideos.map(video => {
              const isUploaded = !!video.storage_path;
              const uploadedThumb = isUploaded && video.thumbnail_storage_path
                ? `${API_URL}/api/videos/${video.id}/thumbnail`
                : null;
              const ytInfo = getYouTubeInfo(video.video_url || '');
              const thumbnail = video.thumbnail_url
                || uploadedThumb
                || ytInfo?.thumbnail
                || 'https://via.placeholder.com/400x225?text=Video';

              return (
                <div
                  key={video.id}
                  className="bg-white rounded-2xl shadow-sm overflow-hidden group cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => setSelectedVideo(video)}
                  data-testid={`video-card-${video.id}`}
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video">
                    <img
                      src={thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/400x225?text=Video'; }}
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="bg-white rounded-full p-4 shadow-lg">
                        <Play className="w-8 h-8" style={{ color: config.color }} fill={config.color} />
                      </div>
                    </div>
                    {video.duration && (
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-sm flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {video.duration}
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-4">
                    <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: config.color }}>
                      {getCategoryName(video.category_id)}
                    </span>
                    <h3 className="font-bold text-gray-800 mt-1 line-clamp-2">{video.title}</h3>
                    {video.description && (
                      <p className="text-sm text-gray-500 mt-2 line-clamp-2">{video.description}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: config.color }}>
                  {getCategoryName(selectedVideo.category_id)}
                </span>
                <h3 className="font-bold text-lg text-gray-800">{selectedVideo.title}</h3>
              </div>
              <button 
                onClick={() => setSelectedVideo(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Video Player */}
            <div className="aspect-video bg-black">
              {selectedVideo.storage_path ? (
                // Uploaded video — stream through our backend so it stays
                // auth-gated (no leaking direct-storage URLs). `preload=metadata`
                // keeps initial payload small for slow mobile connections.
                <video
                  key={selectedVideo.id}
                  src={`${API_URL}/api/videos/${selectedVideo.id}/stream`}
                  poster={selectedVideo.thumbnail_storage_path ? `${API_URL}/api/videos/${selectedVideo.id}/thumbnail` : undefined}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  className="w-full h-full"
                  data-testid={`video-player-${selectedVideo.id}`}
                >
                  Your browser doesn't support HTML5 video.
                </video>
              ) : getYouTubeInfo(selectedVideo.video_url) ? (
                <iframe
                  src={`${getYouTubeInfo(selectedVideo.video_url).embedUrl}?autoplay=1`}
                  title={selectedVideo.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white">
                  <a
                    href={selectedVideo.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 rounded-xl hover:opacity-90 transition-opacity text-white"
                    style={{ backgroundColor: config.color }}
                  >
                    <Play className="w-5 h-5" />
                    Open Video
                  </a>
                </div>
              )}
            </div>
            
            {/* Description */}
            {selectedVideo.description && (
              <div className="p-4 border-t">
                <p className="text-gray-600">{selectedVideo.description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoLibrary;
